import { useState } from "react";
import { useChildScope } from "@/context/ChildScopeContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChevronDown, Users, Check, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";

// v65 Phase 1 — top-right child switcher.
//
// Behaves like an account switcher: shows the current scope (All
// children OR a single child's name) and lets the user flip between
// them. Master view is always available to non-mediator non-operator
// accounts in Phase 1; Phase 2 narrows the list to the user's actual
// child memberships and hides "All children" for co-parents.
//
// The component intentionally never uses the word "Space" — copy is
// "All children" or the child's first name, per the locked decision.
export function ChildSwitcher({ className = "", testIdSuffix = "" }) {
  const { user, logout } = useAuth();
  const { children, selectedChildId, selectedChild, setSelectedChildId } = useChildScope();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Operators and mediators don't have a child scope.
  if (!user || user.is_primary_admin || user.role === "mediator") return null;
  // No children added yet — switcher would be empty noise.
  if (children.length === 0) return null;

  const label = selectedChild ? selectedChild.name : "All children";
  const initials = (selectedChild?.name || user?.name || "?").trim().slice(0, 1).toUpperCase();

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate("/");
  };

  const handlePick = (id) => {
    setSelectedChildId(id);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`h-9 pl-1.5 pr-2.5 gap-2 rounded-full transition-colors ${
            selectedChild
              // v77.99.61 — Distinct filtered state. Sand-tinted pill so
              // it's obvious at a glance that the global view is
              // narrowed to one child. Matches the in-page "Filtered:"
              // chip on Insights for consistency.
              ? "border-[#E8D6A8] bg-[#FBF5E8] hover:bg-[#F5EAD2]"
              : "border-[#D9E1E3] bg-white hover:bg-[#EEF3F4]"
          } ${className}`}
          data-testid={`child-switcher-trigger${testIdSuffix}`}
          data-filtered={selectedChild ? "1" : "0"}
          aria-label={`Viewing: ${label}. Click to switch.`}
        >
          <span
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold text-white"
            style={{ backgroundColor: selectedChild?.color || "#1F9FA4" }}
            aria-hidden="true"
          >
            {selectedChild ? initials : <Users className="w-3 h-3" strokeWidth={2} />}
          </span>
          <span className="hidden sm:inline-block max-w-[140px] truncate text-[13px] font-medium text-[#2E3A3F]">
            {label}
          </span>
          <ChevronDown className="w-3.5 h-3.5 text-[#6F828A]" strokeWidth={1.8} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-64 bg-white border-[#D9E1E3]"
        data-testid="child-switcher-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] font-medium">
          Viewing
        </DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={() => handlePick(null)}
          className="cursor-pointer focus:bg-[#EEF3F4]"
          data-testid="child-switcher-all"
        >
          <span
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1F9FA4] text-white mr-2.5 flex-shrink-0"
            aria-hidden="true"
          >
            <Users className="w-3 h-3" strokeWidth={2} />
          </span>
          <span className="flex-1 text-sm">All children</span>
          {!selectedChildId && <Check className="w-3.5 h-3.5 text-[#1F9FA4]" strokeWidth={2} />}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#D9E1E3]" />
        {children.map((c) => {
          const isArchived = !!c.archived_at;
          return (
            <DropdownMenuItem
              key={c.id}
              onSelect={() => handlePick(c.id)}
              className={`cursor-pointer focus:bg-[#EEF3F4] ${isArchived ? "opacity-70" : ""}`}
              data-testid={`child-switcher-item-${c.id}`}
            >
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold text-white mr-2.5 flex-shrink-0"
                style={{ backgroundColor: c.color || "#1F9FA4" }}
                aria-hidden="true"
              >
                {(c.name || "?").trim().slice(0, 1).toUpperCase()}
              </span>
              <span className="flex-1 text-sm truncate">
                {c.name}
                {isArchived && (
                  <span className="ml-1.5 text-[9px] uppercase tracking-[0.1em] text-[#6F828A]">archived</span>
                )}
              </span>
              {selectedChildId === c.id && <Check className="w-3.5 h-3.5 text-[#1F9FA4]" strokeWidth={2} />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator className="bg-[#D9E1E3]" />
        <DropdownMenuItem
          onSelect={handleLogout}
          className="cursor-pointer focus:bg-[#EEF3F4] text-[#6F828A]"
          data-testid="child-switcher-logout"
        >
          <LogOut className="w-3.5 h-3.5 mr-2.5" strokeWidth={1.6} />
          <span className="text-sm">Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
